import { Fragment, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, CalendarDays, PartyPopper } from "lucide-react";
import { toast } from "sonner";
import { typeBadgeClass, fmtRange } from "@/components/HolidaysList";

const EMPTY = { name: "", type: "National", start_date: "", end_date: "", note: "" };

export default function AdminHolidays({ embedded } = {}) {
  const [holidays, setHolidays] = useState([]);
  const [types, setTypes] = useState(["National", "Festival", "Exam", "Vacation", "Event", "Other"]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // null | {mode:'create'} | {mode:'edit', id}
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/holidays");
      setHolidays(data.holidays || []);
      if (data.types?.length) setTypes(data.types);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Could not load holidays"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(EMPTY); setDialog({ mode: "create" }); };
  const openEdit = (h) => {
    setForm({ name: h.name, type: h.type, start_date: h.start_date, end_date: h.end_date || "", note: h.note || "" });
    setDialog({ mode: "edit", id: h.id });
  };

  const save = async () => {
    const name = form.name.trim();
    if (!name) { toast.error("Holiday name is required."); return; }
    if (!form.start_date) { toast.error("Start date is required."); return; }
    if (form.end_date && form.end_date < form.start_date) { toast.error("End date can't be before start date."); return; }
    setSaving(true);
    const payload = {
      name, type: form.type, start_date: form.start_date,
      end_date: form.end_date || form.start_date, note: form.note.trim() || null,
    };
    try {
      if (dialog.mode === "create") await api.post("/holidays", payload);
      else await api.patch(`/holidays/${dialog.id}`, payload);
      toast.success(dialog.mode === "create" ? "Holiday added." : "Holiday updated.");
      setDialog(null);
      await load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    setDeletingId(id);
    try {
      await api.delete(`/holidays/${id}`);
      toast.success("Holiday deleted.");
      setHolidays((prev) => prev.filter((h) => h.id !== id));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Delete failed"); }
    finally { setDeletingId(null); }
  };

  const Wrap = embedded ? Fragment : "div";
  return (
    <Wrap>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
        <div>
          <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Holidays</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">Add, edit or remove school holidays. Teachers and students see these read-only.</p>
        </div>
        <Button onClick={openCreate} className="bg-sky-500 hover:bg-sky-600" data-testid="holiday-add-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Add holiday
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-500">Loading…</div>
      ) : holidays.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-200 dark:border-slate-700 p-10 text-center text-slate-500" data-testid="admin-holidays-empty">
          No holidays yet. Click “Add holiday” to create one.
        </div>
      ) : (
        <div className="space-y-2" data-testid="admin-holidays-list">
          {holidays.map((h) => (
            <div key={h.id} className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-3" data-testid={`admin-holiday-${h.id}`}>
              <div className="h-9 w-9 rounded-lg bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-300 flex items-center justify-center shrink-0">
                <PartyPopper className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-slate-900 dark:text-slate-100 truncate">{h.name}</span>
                  <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${typeBadgeClass(h.type)}`}>{h.type}</span>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 inline-flex items-center gap-1.5 mt-0.5">
                  <CalendarDays className="h-3 w-3" /> {fmtRange(h.start_date, h.end_date)}
                  {h.note && <span className="truncate">· {h.note}</span>}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="ghost" size="sm" className="rounded-full" onClick={() => openEdit(h)} data-testid={`holiday-edit-${h.id}`}>
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button variant="ghost" size="sm" className="rounded-full text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40" onClick={() => remove(h.id)} disabled={deletingId === h.id} data-testid={`holiday-delete-${h.id}`}>
                  {deletingId === h.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => { if (!o) setDialog(null); }}>
        <DialogContent data-testid="holiday-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.mode === "edit" ? "Edit holiday" : "Add holiday"}</DialogTitle>
            <DialogDescription>Set the date(s), type and an optional note.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Name</div>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Independence Day" data-testid="holiday-name-input" autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Type</div>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="holiday-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {types.map((t) => <SelectItem key={t} value={t} data-testid={`holiday-type-${t}`}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Start date</div>
                <DatePicker value={form.start_date} onChange={(v) => setForm({ ...form, start_date: v })} data-testid="holiday-start-input" />
              </div>
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">End date <span className="font-normal text-slate-400">(optional · for multi-day)</span></div>
              <DatePicker value={form.end_date} min={form.start_date || undefined} onChange={(v) => setForm({ ...form, end_date: v })} placeholder="Same as start" data-testid="holiday-end-input" />
            </div>
            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Note <span className="font-normal text-slate-400">(optional)</span></div>
              <Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Any details for teachers & students" rows={2} data-testid="holiday-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={saving}>Cancel</Button>
            <Button className="bg-sky-500 hover:bg-sky-600" onClick={save} disabled={saving || !form.name.trim() || !form.start_date} data-testid="holiday-save-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Wrap>
  );
}
